/**
 * @fileoverview AR-670 — standalone "Link to Google Classroom" modal
 * (prototype: components/Projects/LinkGoogleClassroomModal.tsx). Link-only:
 * course creation does not exist in the read-only backend. Courses come from
 * the local synced catalog; ones already linked to another project are
 * filtered out (1 course = 1 project — linking them would 422).
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Alert, Anchor, Button, Group, Loader, Modal, Select, Stack, Text } from '@mantine/core'
import { IconAlertTriangle } from '@tabler/icons-react'

import { isLinkableCourse, SchoolConnectionStatus, useClassroomCourses } from 'integrations/classroom'

interface LinkClassroomCourseModalProps {
  readonly opened: boolean
  readonly onClose: () => void
  readonly connectionStatus: SchoolConnectionStatus
  /** Resolves when linked (modal closes); rejections go to onError and keep it open. */
  readonly onLink: (courseId: string) => Promise<void>
  readonly onError: (error: unknown) => void
}

export function LinkClassroomCourseModal({
  opened,
  onClose,
  connectionStatus,
  onLink,
  onError,
}: LinkClassroomCourseModalProps) {
  const { t } = useTranslation()
  const courses = useClassroomCourses()
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const tr = (key: string) => t(`classroom.${key}`)
  const { load } = courses

  useEffect(() => {
    if (!opened) return
    setSelectedCourseId(null)
    if (connectionStatus === 'connected') load()
  }, [opened, connectionStatus, load])

  const handleClose = () => {
    if (!submitting) onClose()
  }

  const handleSubmit = async () => {
    if (!selectedCourseId) return
    setSubmitting(true)
    try {
      await onLink(selectedCourseId)
      onClose()
    } catch (error) {
      onError(error)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal opened={opened} onClose={handleClose} title={tr('linkToGoogleClassroom')} centered>
      <Stack spacing='md'>
        {connectionStatus === 'disconnected' && (
          <Stack spacing='xs' align='flex-start'>
            {/* The connection is per user: the caller connects their own
                Google account — no manager needed. */}
            <Text size='sm' color='dimmed'>
              {tr('projectClassroomConnectYourAccount')}
            </Text>
            <Anchor component={Link} to='/teacher/google-classroom' size='sm'>
              {tr('gcwGoToConnectionPage')}
            </Anchor>
          </Stack>
        )}

        {connectionStatus === 'needs_reconnection' && (
          <Alert color='orange' variant='light'>
            {tr('projectClassroomNeedsReconnection')}
          </Alert>
        )}

        {connectionStatus === 'connected' && (
          <Stack spacing='sm'>
            <Text size='sm' color='dimmed'>
              {tr('linkToGoogleClassroomDescription')}
            </Text>
            {/* The catalog answers 200 with cached courses while the OAuth
                grant is expired — linking would still fail, so say so. */}
            {courses.connectionStatus === 'needs_reconnection' && (
              <Alert color='orange' variant='light'>
                {tr('projectClassroomNeedsReconnection')}
              </Alert>
            )}
            <Alert color='yellow' variant='light' icon={<IconAlertTriangle size={16} />}>
              {tr('linkToGoogleClassroomWarning')}
            </Alert>
            {courses.failed ? (
              // 422 only happens with an EMPTY catalog: "never connected" gets
              // its own copy; anything else is a real failure worth retrying.
              courses.failedCode === 'not_connected' ? (
                <Text size='sm' color='dimmed'>
                  {tr('projectClassroomConnectYourAccount')}
                </Text>
              ) : (
                <Group spacing='xs'>
                  <Text size='sm' color='red'>
                    {tr('classroomLoadCoursesFailed')}
                  </Text>
                  <Button compact variant='subtle' onClick={() => load()}>
                    {tr('projectClassroomRefresh')}
                  </Button>
                </Group>
              )
            ) : (
              <Select
                label={tr('classroomAssignSelectCourse')}
                placeholder={tr('classroomAssignSelectCourse')}
                data={courses.courses.filter(isLinkableCourse).map(course => ({
                  value: course.id,
                  label: course.section ? `${course.name} — ${course.section}` : course.name,
                }))}
                value={selectedCourseId}
                onChange={setSelectedCourseId}
                searchable
                disabled={courses.loading}
                rightSection={courses.loading ? <Loader size='xs' /> : undefined}
                nothingFound={tr('classroomPickerNoCourses')}
              />
            )}
          </Stack>
        )}

        <Group position='right'>
          <Button variant='default' onClick={handleClose} disabled={submitting}>
            {tr('cancel')}
          </Button>
          {connectionStatus === 'connected' && (
            <Button onClick={handleSubmit} loading={submitting} disabled={!selectedCourseId || courses.loading}>
              {tr('projectClassroomLinkCourse')}
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  )
}
